import { useId, useLayoutEffect, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Skeleton } from '@monksflow/monks-ui';

import type { SeriesPoint } from '../../lib/series';

export interface MetricChartProps {
  data: SeriesPoint[];
  /** Legend/tooltip name for the primary area. */
  primaryLabel: string;
  /** Supplying this stacks a second area (sent vs received). */
  secondaryLabel?: string;
  /** Averages need a decimal; counts do not. */
  allowDecimals?: boolean;
}

/**
 * All design-system tokens, defined per light/dark mode in `styles/global.css`.
 *
 * A single-series card carries no identity in its colour, so all five share one
 * step - varying the hue per card would imply a categorical relationship that
 * does not exist. Only the sent/received chart is genuinely categorical, and it
 * uses the validated two-step pair.
 *
 * Deliberately not --primary: that is the brand action colour, and the neutral
 * theme repointed it at neutral-900, which painted black strokes over the blue
 * --chart-fill.
 */
const SERIES_COLOR = 'var(--analytics-series)';
const SERIES_FILL = 'var(--chart-fill)';
const SERIES_B_COLOR = 'var(--analytics-series-b)';

const TICK_FONT_SIZE = 11;

/** Gap between the widest y-axis label and the plot area. */
const Y_AXIS_GAP = 8;

/**
 * The first date label is centred on the plot's left edge, so a very narrow
 * axis (a single-digit scale) pushes half of it outside the SVG and recharts
 * drops the label entirely - leaving that card starting a day later than its
 * neighbours. Half a "Jul 24" label is ~17px.
 */
const Y_AXIS_MIN_WIDTH = 18;

/**
 * Measured width of one digit at TICK_FONT_SIZE in the app font: a 3-character
 * label renders 21.1px wide. Used to size the y-axis, because recharts'
 * `width="auto"` only measures its own tick renderer - with a custom tick
 * element it collapses to a ~14px minimum and the labels run under the plot.
 */
const TICK_CHAR_WIDTH = 7.03;

/**
 * The "nice" value recharts rounds a peak up to when choosing its top tick.
 * A peak of 85 gets a "100" tick, so the label is wider than the peak itself.
 * Rounding to the next power of ten instead would badly over-reserve (a peak
 * of 105 would budget for "1000").
 */
function niceCeiling(peak: number): number {
  if (peak <= 0) return 0;
  const magnitude = 10 ** Math.floor(Math.log10(peak));
  const step = [1, 2, 2.5, 5, 10].find((candidate) => peak <= candidate * magnitude) ?? 10;
  return step * magnitude;
}

/**
 * Width the y-axis needs so its labels clear the plot without leaving a gap.
 * Verified against all six cards in the browser.
 */
function yAxisWidth(data: SeriesPoint[], allowDecimals: boolean): number {
  const peak = data.reduce((max, point) => Math.max(max, point.value + (point.secondary ?? 0)), 0);
  // Decimal ticks ("2.5") only appear once the whole range is single-digit;
  // above that recharts still steps in whole numbers.
  const decimalChars = allowDecimals && peak < 10 ? 2 : 0;
  const characters = String(Math.round(niceCeiling(peak))).length + decimalChars;
  return Math.max(Math.ceil(characters * TICK_CHAR_WIDTH) + Y_AXIS_GAP, Y_AXIS_MIN_WIDTH);
}

// `x` is intentionally absent: recharts supplies it, but this tick ignores it
// and pins itself to the left edge.
interface AxisTickProps {
  y?: number;
  payload?: { value: string | number; coordinate?: number };
}

/**
 * Y-axis ticks pinned to the SVG's left edge (`x=0`) instead of recharts'
 * default, which right-aligns them against the plot area. Right-alignment
 * leaves the numbers ragged and indented relative to the card title; this makes
 * the column's left edge line up with the title above it.
 */
const YAxisTick = ({ y = 0, payload }: AxisTickProps) => (
  <text
    x={0}
    // `payload.coordinate`, not `y`: recharts nudges the topmost tick's `y`
    // downwards (here 4 -> 12) so that a default-anchored label cannot clip at
    // the chart's edge. Its own renderer compensates with `verticalAnchor`;
    // this one would just inherit the nudge and sit 8px below its gridline.
    // The chart's top margin reserves the headroom that nudge was protecting.
    y={payload?.coordinate ?? y}
    // Let the browser centre the glyphs on the gridline rather than
    // approximating it with a hand-computed baseline offset.
    dominantBaseline="central"
    textAnchor="start"
    fontSize={TICK_FONT_SIZE}
    fill="currentColor"
  >
    {payload?.value}
  </text>
);

interface Size {
  width: number;
  height: number;
}

/**
 * The chart area's size, measured once before the browser paints.
 *
 * `ResponsiveContainer` renders nothing until its own observer reports a size,
 * which costs a frame and shows as a blank flash inside an otherwise complete
 * card. Measuring here lets us seed it with `initialDimension` so its first
 * paint already has the chart, and gives us something to show if the element
 * genuinely has no size yet (a hidden tab, say).
 */
function useMeasuredSize(): [React.RefObject<HTMLDivElement | null>, Size | null] {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () => {
      const { width, height } = element.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setSize({ width, height });
        return true;
      }
      return false;
    };

    // Usually resolves synchronously, before the first paint. Only fall back to
    // observing when the element starts out collapsed; recharts handles every
    // later resize itself, so this stops at the first real measurement.
    if (measure()) return undefined;

    const observer = new ResizeObserver(() => {
      if (measure()) observer.disconnect();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  return [ref, size];
}

const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <li className="flex items-center gap-1.5">
    {/* The swatch carries identity; the text stays in an ink token so the
        label is legible regardless of how light the series colour is. */}
    <span aria-hidden className="h-0.5 w-3 rounded-full" style={{ backgroundColor: color }} />
    {label}
  </li>
);

export const MetricChart = ({
  data,
  primaryLabel,
  secondaryLabel,
  allowDecimals = false,
}: MetricChartProps) => {
  // Gradient ids must be unique per rendered chart, otherwise every card reuses
  // the first card's fill.
  const gradientId = useId();

  // Every card's primary line is the same step, so the set reads as one thing;
  // only a genuine second series introduces a second colour.
  const isStacked = Boolean(secondaryLabel);

  const [chartRef, size] = useMeasuredSize();

  return (
    // min-h-0 lets the chart shrink inside the flex column; without it the
    // ResponsiveContainer's measured height only ever grows.
    <div className="flex min-h-0 w-full flex-1 flex-col gap-2">
      {/* Markup rather than recharts' <Legend>, which positions itself against
          the plot area and so sits indented from the card's content edge. Two
          series must never be identifiable by colour alone; a single series
          needs no legend - the card title already names it. */}
      {isStacked && secondaryLabel ? (
        <ul className="flex items-center gap-4 text-xs text-muted-foreground">
          <LegendItem color={SERIES_COLOR} label={primaryLabel} />
          <LegendItem color={SERIES_B_COLOR} label={secondaryLabel} />
        </ul>
      ) : null}

      <div ref={chartRef} className="min-h-40 w-full flex-1 text-muted-foreground">
        {size === null ? (
          <Skeleton className="h-full min-h-40 w-full" />
        ) : (
          <ResponsiveContainer initialDimension={size}>
            {/* No negative left margin: it pulls the y-axis out of the SVG and
              clips the tick labels ("600" renders as "00"). */}
            {/* The top margin is the headroom the topmost tick label needs to sit
              centred on its gridline instead of clipping at the chart edge. */}
            <AreaChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`${gradientId}-primary`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SERIES_COLOR} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={SERIES_COLOR} stopOpacity={0} />
                </linearGradient>
                <linearGradient id={`${gradientId}-secondary`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SERIES_B_COLOR} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={SERIES_B_COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.12} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: TICK_FONT_SIZE, fill: 'currentColor' }}
                minTickGap={16}
              />
              <YAxis
                allowDecimals={allowDecimals}
                tickLine={false}
                axisLine={false}
                tick={<YAxisTick />}
                // Sized to this chart's own scale, so a "0"-"100" axis leaves no
                // dead gap and a four-digit one still clears the plot.
                width={yAxisWidth(data, allowDecimals)}
              />
              <Tooltip cursor={{ strokeOpacity: 0.2 }} isAnimationActive={false} />
              <Area
                type="monotone"
                dataKey="value"
                name={primaryLabel}
                // eslint-disable-next-line i18next/no-literal-string -- Recharts stack id joining the two series
                stackId="metric"
                stroke={SERIES_COLOR}
                strokeWidth={2}
                // A single series uses the DS chart fill; the stacked pair needs
                // per-series gradients so the two bands stay distinguishable.
                fill={isStacked ? `url(#${gradientId}-primary)` : SERIES_FILL}
                isAnimationActive={false}
              />
              {secondaryLabel ? (
                <Area
                  type="monotone"
                  dataKey="secondary"
                  name={secondaryLabel}
                  // eslint-disable-next-line i18next/no-literal-string -- Recharts stack id joining the two series
                  stackId="metric"
                  stroke={SERIES_B_COLOR}
                  strokeWidth={2}
                  fill={`url(#${gradientId}-secondary)`}
                  isAnimationActive={false}
                />
              ) : null}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
